import { Container, Graphics, Sprite, Text, type Texture } from 'pixi.js';
import { BoardPanel } from '../board/BoardPanel';
import { animate, displayStyle, ease, uiStyle } from '../board/ui';

export { columnsFor, gridCells } from '../../logic/minigames/grid';

/**
 * A one-tap explanation, as the road's panel gives: plain words on a small
 * board near what was tapped, for a few seconds (producer 2026-10-14: if a
 * player can't tell what something is for, it isn't done).
 */
export class Explainer extends Container {
  private timer: ReturnType<typeof setTimeout> | null = null;
  /** The words showing, or '' (browser tests). */
  text = '';

  constructor(private readonly floor: () => Texture | null) {
    super();
    this.eventMode = 'none';
    this.visible = false;
  }

  /** Say `text` above `at` (or below it, near the top, or when `below`),
   *  within `width`. */
  show(text: string, at: { x: number; y: number }, width: number, below = false): void {
    if (this.timer) clearTimeout(this.timer);
    this.removeChildren().forEach((c) => c.destroy({ children: true }));
    this.text = text;
    const w = Math.min(300, width);
    const label = new Text({ text, style: { ...uiStyle(14), wordWrap: true, wordWrapWidth: w - 24, lineHeight: 18 } });
    const board = new BoardPanel({ floor: this.floor(), radius: 10 });
    const h = label.height + 20;
    board.resize(w, h);
    label.position.set(12, 10);
    this.addChild(board, label);
    const x = Math.max(0, Math.min(width - w, at.x - w / 2));
    const y = !below && at.y - h - 12 >= 0 ? at.y - h - 12 : at.y + (below ? 8 : 24);
    this.position.set(Math.round(x), Math.round(y));
    this.alpha = 0;
    this.visible = true;
    void animate(160, (t) => (this.alpha = t));
    this.timer = setTimeout(() => this.hide(), 4500);
  }

  hide(): void {
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    this.text = '';
    if (!this.visible) return;
    void animate(200, (t) => (this.alpha = 1 - t)).then(() => {
      if (!this.text) this.visible = false;
    });
  }
}

/**
 * A game's counters in one row ("Tries left 6", "Total 0"): each a small
 * label and its number. Tapping the row says what they mean.
 */
export class CounterRow extends Container {
  private readonly items: { label: Text; value: Text }[];

  constructor(labels: string[], onTap: (at: { x: number; y: number }) => void) {
    super();
    this.items = labels.map((l) => {
      const label = new Text({ text: l, style: uiStyle(14, 0xf0dcb4, '600') });
      const value = new Text({ text: '', style: displayStyle(19) });
      label.anchor.set(0, 0.5);
      value.anchor.set(0, 0.5);
      this.addChild(label, value);
      return { label, value };
    });
    this.eventMode = 'static';
    this.cursor = 'help';
    this.on('pointertap', (e) => {
      const p = this.toLocal(e.global);
      onTap({ x: this.x + p.x, y: this.y });
    });
  }

  /** Show `values`, centred across `width`, from y = 0. */
  set(values: string[], width: number): void {
    const gap = 26;
    const widths = this.items.map((it, i) => {
      it.value.text = values[i] ?? '';
      return it.label.width + 6 + it.value.width;
    });
    const total = widths.reduce((a, b) => a + b, 0) + gap * (this.items.length - 1);
    let x = (width - total) / 2;
    const mid = this.rowHeight / 2;
    this.items.forEach((it, i) => {
      it.label.position.set(Math.round(x), mid);
      it.value.position.set(Math.round(x + it.label.width + 6), mid);
      x += widths[i]! + gap;
    });
    this.hitArea = { contains: (px: number, py: number) => px >= (width - total) / 2 - 8 && px <= (width + total) / 2 + 8 && py >= -6 && py <= this.rowHeight + 6 };
  }

  get rowHeight(): number {
    return 26;
  }
}

/**
 * One tappable game piece: its art (or a drawn stand-in until the art
 * loads), a soft floor shadow, and a label over it.
 */
export class Piece extends Container {
  readonly body = new Container();
  readonly sprite = new Sprite();
  readonly drawn = new Graphics();
  readonly shadow = new Graphics();
  readonly caption: Text;
  w = 80;
  h = 80;

  constructor(onTap: () => void) {
    super();
    this.sprite.anchor.set(0.5);
    this.caption = new Text({ text: '', style: displayStyle(20) });
    this.caption.anchor.set(0.5);
    this.body.addChild(this.drawn, this.sprite);
    this.addChild(this.shadow, this.body, this.caption);
    this.eventMode = 'static';
    this.cursor = 'pointer';
    this.on('pointertap', onTap);
  }

  /** Size the piece and show `texture`, or `fallback` drawn while it loads. */
  paint(texture: Texture | null, w: number, h: number, fallback: (g: Graphics, w: number, h: number) => void, shadow = true): void {
    this.w = w;
    this.h = h;
    this.hitArea = { contains: (x: number, y: number) => Math.abs(x) <= w / 2 && Math.abs(y) <= h / 2 };
    this.shadow.clear();
    if (shadow) this.shadow.ellipse(0, h * 0.4, w * 0.32, h * 0.06).fill({ color: 0x000000, alpha: 0.4 });
    if (texture) {
      this.sprite.texture = texture;
      const k = Math.min(w / texture.width, h / texture.height);
      this.sprite.scale.set(k);
      this.sprite.visible = true;
      this.drawn.clear();
    } else {
      this.sprite.visible = false;
      this.drawn.clear();
      fallback(this.drawn, w, h);
    }
  }

  /** A quick squash, as when a piece is hit. */
  async bump(): Promise<void> {
    await animate(200, (t) => {
      const k = 1 - 0.18 * Math.sin(t * Math.PI);
      this.body.scale.set(1 + (1 - k) * 0.6, k);
    });
    this.body.scale.set(1);
  }

  /** Pop in (a reveal). */
  async pop(target: Container = this.body): Promise<void> {
    await animate(360, (t) => target.scale.set(0.7 + 0.3 * ease.outBack(t)));
  }
}
